import { useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '../../lib/api-client';
import { useLogin } from '../../lib/api';
import { AuthLayout } from './auth-layout';
import { ProviderSignIn, oauthErrorMessage } from '../../components/oauth-buttons';
import { Button, Field, FormError, TextInput } from '../../components/ui';

const schema = z.object({
  email: z.email('Enter a valid email address.'),
  // No length rule on login: an existing password predates any rule change, and rejecting it here
  // would lock out the account rather than telling the customer their password is wrong.
  password: z.string().min(1, 'Required.'),
});

type LoginForm = z.infer<typeof schema>;

export function LoginRoute() {
  const login = useLogin();
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });
  // A Google sign-in that did not complete comes back here with the reason in the query string.
  const [formError, setFormError] = useState<string | null>(() =>
    oauthErrorMessage(search.oauth_error),
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit((values) => {
    setFormError(null);
    login.mutate(values, {
      onSuccess: () => {
        // Return the customer to wherever the guard interrupted them, defaulting to the overview.
        // Only an in-app path is honoured. Following an arbitrary value from the query string
        // would make this an open redirect.
        const target = search.redirect;
        const isSafe = target !== undefined && target.startsWith('/');
        void navigate({ to: isSafe ? (target as '/overview') : '/overview' });
      },
      onError: (error) => {
        setFormError(
          error instanceof ApiError ? error.message : 'Could not sign in. Please try again.',
        );
      },
    });
  });

  return (
    <AuthLayout
      title="Sign in"
      description="Use your urlshot.io account."
      footer={
        <div className="flex flex-col gap-1">
          <span>
            No account?{' '}
            <Link to="/register" className="link">
              Create one
            </Link>
          </span>
          <Link to="/forgot-password" className="link">
            Forgot your password?
          </Link>
        </div>
      }
    >
      {/* One column: a piece that renders nothing (no error, no Google) takes no space. */}
      <div className="flex flex-col gap-4">
        <FormError message={formError} />
        <ProviderSignIn redirect={search.redirect} />

        <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
          <Field label="Email" htmlFor="email" error={errors.email?.message}>
            <TextInput
              id="email"
              type="email"
              autoComplete="email"
              invalid={errors.email !== undefined}
              {...register('email')}
            />
          </Field>

          <Field label="Password" htmlFor="password" error={errors.password?.message}>
            <TextInput
              id="password"
              type="password"
              autoComplete="current-password"
              invalid={errors.password !== undefined}
              {...register('password')}
            />
          </Field>

          <Button type="submit" disabled={isSubmitting || login.isPending}>
            {login.isPending ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
